import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Waves, Play, Loader2, ArrowDown, Zap } from "lucide-react";
import api from "../../../lib/api";
import { useAuth } from "../../../lib/auth";
import { ROLES } from "../../../lib/roles";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";

function money(n, cur = "MXN") {
    return Number(n || 0).toLocaleString("es-MX", {
        style: "currency", currency: cur, maximumFractionDigits: 0,
    });
}

export default function WaterfallTab({ facility }) {
    const { hasRole } = useAuth();
    const [config, setConfig] = useState({ cascada: [] });
    const [ejecuciones, setEjecuciones] = useState([]);
    const [monto, setMonto] = useState("1000000");
    const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
    const [simulacion, setSimulacion] = useState(null);
    const [busy, setBusy] = useState(false);

    const canSimulate = hasRole([ROLES.ADMINISTRADOR_FIDUCIARIO, ROLES.TESORERIA, ROLES.ADMIN_TI]);

    const load = async () => {
        const [{ data: cfg }, { data: ej }] = await Promise.all([
            api.get(`/facilities/${facility.id}/waterfall/config`),
            api.get(`/facilities/${facility.id}/waterfall/ejecuciones`),
        ]);
        setConfig(cfg && cfg.cascada ? cfg : { cascada: [] });
        setEjecuciones(ej);
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [facility.id]);

    const simular = async () => {
        if (!monto || Number(monto) <= 0) return toast.error("Monto > 0");
        setBusy(true);
        try {
            const { data } = await api.post(`/facilities/${facility.id}/waterfall/simular`, {
                monto: Number(monto), fecha,
            });
            setSimulacion(data);
            toast.success(`Cascada simulada · asignado ${money(data.total_asignado, facility.moneda)}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    const ejecutar = async () => {
        if (!simulacion) return toast.error("Primero simula");
        if (!window.confirm(`Ejecutar cascada por ${money(monto, facility.moneda)}? Se crearán ${simulacion.lineas.reduce((s, l) => s + l.pagos.length, 0)} instrucciones de pago.`)) return;
        setBusy(true);
        try {
            const { data } = await api.post(`/facilities/${facility.id}/waterfall/ejecutar`, {
                monto: Number(monto), fecha,
            });
            toast.success(`Ejecución creada · ${data.instrucciones_ids.length} instrucciones en borrador`);
            setSimulacion(null);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Error");
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-6" data-testid="tab-waterfall-content">
            <div className="flex items-center gap-2">
                <Waves className="h-5 w-5 text-[color:var(--brand-dark-gold)]" />
                <h3 className="font-display text-2xl">Waterfall (cascada de pagos)</h3>
            </div>

            {/* Config actual */}
            <div className="rounded-md border border-border bg-card overflow-hidden">
                <div className="px-4 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground">
                    Cascada activa · v{config.version || 0} · {config.cascada?.length || 0} líneas
                </div>
                <table className="trf-table" data-testid="waterfall-config-table">
                    <thead>
                        <tr>
                            <th>#</th><th>Concepto</th><th>Tope monto</th><th>Tope %</th>
                            <th>Pro-rata</th><th>Beneficiarios</th>
                        </tr>
                    </thead>
                    <tbody>
                        {(config.cascada || []).map((l) => (
                            <tr key={l.orden}>
                                <td className="tabular font-medium">{l.orden}</td>
                                <td>{l.concepto}</td>
                                <td className="tabular">{l.tope_monto !== null && l.tope_monto !== undefined ? money(l.tope_monto, facility.moneda) : "—"}</td>
                                <td className="tabular">{l.tope_pct !== null && l.tope_pct !== undefined ? `${l.tope_pct}%` : "—"}</td>
                                <td>{l.pro_rata ? <span className="badge-gold">Pro-rata</span> : "—"}</td>
                                <td className="text-xs">
                                    {(l.beneficiarios || []).length > 0
                                        ? l.beneficiarios.map((b) => `${b.pct}%`).join(", ")
                                        : "—"}
                                </td>
                            </tr>
                        ))}
                        {(!config.cascada || config.cascada.length === 0) && (
                            <tr><td colSpan={6} className="text-center py-6 text-muted-foreground">
                                Sin cascada configurada.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Simulador */}
            {canSimulate && config.cascada?.length > 0 && (
                <div className="rounded-md border border-border bg-card p-4 space-y-3" data-testid="simulador-panel">
                    <h4 className="font-display text-lg">Simulador</h4>
                    <div className="grid md:grid-cols-3 gap-3">
                        <div><Label>Monto a distribuir ({facility.moneda})</Label>
                            <Input type="number" value={monto}
                                onChange={(e) => setMonto(e.target.value)}
                                data-testid="waterfall-monto" />
                        </div>
                        <div><Label>Fecha</Label>
                            <Input type="date" value={fecha}
                                onChange={(e) => setFecha(e.target.value)} />
                        </div>
                        <div className="flex items-end gap-2">
                            <Button onClick={simular} disabled={busy} data-testid="simular-btn">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Play className="h-4 w-4 mr-1" /> Simular</>)}
                            </Button>
                            {simulacion && (
                                <Button variant="destructive" onClick={ejecutar} disabled={busy} data-testid="ejecutar-btn">
                                    <Zap className="h-4 w-4 mr-1" /> Ejecutar
                                </Button>
                            )}
                        </div>
                    </div>

                    {simulacion && (
                        <div className="pt-3 border-t border-border" data-testid="simulacion-result">
                            <div className="flex items-center justify-between mb-3">
                                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                    Distribución
                                </div>
                                <div className="text-xs">
                                    Remanente: <span className="tabular font-medium">{money(simulacion.remanente, facility.moneda)}</span>
                                </div>
                            </div>
                            <div className="space-y-2">
                                {simulacion.lineas.map((l, idx) => (
                                    <div key={idx} className="rounded-sm border border-border p-3 bg-[color:var(--brand-paper)]/40" data-testid={`sim-linea-${l.orden}`}>
                                        <div className="flex items-baseline justify-between">
                                            <div className="text-sm">
                                                <span className="font-medium">#{l.orden}</span> · {l.concepto}
                                            </div>
                                            <div className="font-display text-lg tabular">
                                                {money(l.monto_asignado, facility.moneda)}
                                            </div>
                                        </div>
                                        <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
                                            <ArrowDown className="h-3 w-3" />
                                            {l.pagos.length} pago{l.pagos.length !== 1 ? "s" : ""}
                                            {l.pagos.map((p, i) => (
                                                <span key={i} className="hash-cell ml-2">
                                                    → {p.persona_id?.slice(0, 8) || "sin-persona"}: {money(p.monto, facility.moneda)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Histórico ejecuciones */}
            {ejecuciones.length > 0 && (
                <div className="rounded-md border border-border bg-card overflow-hidden">
                    <div className="px-4 py-2 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground">
                        Ejecuciones históricas
                    </div>
                    <table className="trf-table text-xs">
                        <thead><tr>
                            <th>Fecha</th><th>Monto</th><th>Config v</th>
                            <th>Instrucciones</th><th>Remanente</th><th>Estado</th>
                        </tr></thead>
                        <tbody>
                            {ejecuciones.map((e) => (
                                <tr key={e.id} data-testid={`ejec-row-${e.id}`}>
                                    <td className="tabular">{e.fecha}</td>
                                    <td className="text-right tabular">{money(e.monto_input, facility.moneda)}</td>
                                    <td>v{e.config_version}</td>
                                    <td className="tabular">{e.instrucciones_ids?.length || 0}</td>
                                    <td className="tabular">{money(e.remanente, facility.moneda)}</td>
                                    <td><span className="badge-gold">{e.estado}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
